import api from "services/api";

const unwrapList = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.Items)) return payload.Items;
  return [];
};

const unwrapItem = (response) => response?.data?.data ?? response?.data?.Data ?? response?.data;

const getMessage = (error, fallback) => {
  const value =
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data ||
    error?.message;

  return typeof value === "string" ? value : fallback;
};

const normalizeBool = (value) => value === true || value === "true";
const normalizeCommuneName = (value) => String(value ?? "").normalize("NFC").trim().toLocaleLowerCase("vi-VN");

export const catalogApi = {
  async findDuplicateCommune(provinceId, communeName, excludeId = null) {
    const name = normalizeCommuneName(communeName);
    const pageSize = 1000;
    let pageNumber = 1;
    while (true) {
      const response = await api.get("/Commune", { params: {
        provinceId, searchTerm: communeName.slice(0, 200), pageSize, pageNumber,
      } });
      const items = unwrapList(response);
      const duplicate = items.find((item) =>
        Number(item.communeId ?? item.CommuneId) !== Number(excludeId) &&
        Number(item.provinceId ?? item.ProvinceId) === Number(provinceId) &&
        normalizeCommuneName(item.communeName ?? item.CommuneName) === name);
      if (duplicate) return duplicate;
      const totalCount = response.data?.totalCount ?? response.data?.TotalCount;
      if (items.length < pageSize || (totalCount != null && pageNumber * pageSize >= totalCount)) return null;
      pageNumber += 1;
    }
  },
  async page(endpoint, params = {}) {
    const response = await api.get(endpoint, { params });
    return { items: unwrapList(response), totalCount: response.data?.totalCount ?? 0 };
  },
  async getDepartmentTypes() {
    return unwrapList(await api.get("/Department/types"));
  },
  async list(endpoint, params = {}) {
    return unwrapList(await api.get(endpoint, { params }));
  },
  async create(endpoint, data) {
    return unwrapItem(await api.post(endpoint, data));
  },
  async update(endpoint, id, data) {
    return unwrapItem(await api.put(`${endpoint}/${id}`, data));
  },
  async remove(endpoint, id) {
    return api.delete(`${endpoint}/${id}`);
  },
  async getDepartmentUnits(departmentId) {
    return unwrapList(await api.get(`/DepartmentUnit/department/${departmentId}`));
  },
  async createDepartmentUnit(data) {
    return unwrapItem(await api.post("/DepartmentUnit", data));
  },
  async updateDepartmentUnit(id, data) {
    return unwrapItem(await api.put(`/DepartmentUnit/${id}`, data));
  },
  async deleteDepartmentUnit(id) {
    return api.delete(`/DepartmentUnit/${id}`);
  },
  async getGroupUsers(groupId) {
    return unwrapList(await api.get(`/SubcontractExecutionGroup/${groupId}/users`));
  },
  async addGroupUser(groupId, data) {
    return unwrapItem(await api.post(`/SubcontractExecutionGroup/${groupId}/users`, data));
  },
  async updateGroupUser(groupUserId, data) {
    return unwrapItem(await api.put(`/SubcontractExecutionGroup/users/${groupUserId}`, data));
  },
  async deleteGroupUser(groupUserId) {
    return api.delete(`/SubcontractExecutionGroup/users/${groupUserId}`);
  },
};

const nameCatalog = (endpoint, title, idKey = "id", nameKey = "name", maxLength = 250, withNote = true) => ({
  endpoint, title, idKey, addLabel: `Thêm ${title}`,
  normalize: (item) => ({
    [idKey]: item[idKey] ?? item[idKey[0].toUpperCase() + idKey.slice(1)],
    [nameKey]: item[nameKey] ?? item[nameKey[0].toUpperCase() + nameKey.slice(1)] ?? "",
    note: item.note ?? item.Note ?? "",
  }),
  toPayload: (values) => ({
    [nameKey]: String(values[nameKey] || "").trim(),
    ...(withNote ? { note: String(values.note || "").trim() || null } : {}),
  }),
  fields: [
    { name: nameKey, label: `Tên ${title}`, required: true, maxLength },
    ...(withNote ? [{ name: "note", label: "Ghi chú", multiline: true, maxLength: 500 }] : []),
  ],
  columns: [
    { accessorKey: nameKey, header: `Tên ${title}`, size: 300 },
    ...(withNote ? [{ accessorKey: "note", header: "Ghi chú", size: 360 }] : []),
  ],
});

export const normalizeResponseError = getMessage;

export const catalogConfigs = {
  enterprises: nameCatalog("/Enterprise", "đơn vị", "enterpriseId", "name", 255),
  titles: nameCatalog("/ListTitleName", "chức vụ"),
  majors: nameCatalog("/ListMajorName", "chuyên ngành"),
  qualifications: nameCatalog("/ListQualificationName", "trình độ - bằng cấp", "qualificationId", "qualificationName"),
  certificateTypes: nameCatalog("/ListCertificationName", "loại chứng chỉ"),
  provinces: {
    ...nameCatalog("/Province", "tỉnh / thành phố", "provinceId", "provinceName", 255, false),
    serverPagination: true,
  },
  communes: {
    ...nameCatalog("/Commune", "phường / xã", "communeId", "communeName", 255, false),
    serverPagination: true,
    lookupEndpoint: "/Province",
    lookupKey: "provinceId",
    lookupLabel: "provinceName",
    normalize: (item) => ({ communeId: item.communeId, communeName: item.communeName, provinceId: item.provinceId }),
    toPayload: (values) => ({ communeName: String(values.communeName || "").trim(), provinceId: Number(values.provinceId) }),
    fields: [
      { name: "provinceId", label: "Tỉnh / Thành phố", type: "select", required: true, options: [] },
      { name: "communeName", label: "Tên phường / xã", required: true, maxLength: 255 },
    ],
    columns: [
      { accessorKey: "communeName", header: "Phường / Xã", size: 300 },
      { accessorKey: "provinceName", header: "Tỉnh / Thành phố", size: 300 },
    ],
  },
  contractGroups: {
    endpoint: "/ContractGroup",
    title: "nhóm hợp đồng",
    addLabel: "Thêm nhóm hợp đồng",
    idKey: "id",
    normalize: (item) => ({
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name ?? "",
      note: item?.note ?? item?.Note ?? "",
    }),
    toPayload: (values) => ({
      name: String(values.name || "").trim(),
      note: String(values.note || "").trim() || null,
    }),
    fields: [
      { name: "name", label: "Tên nhóm hợp đồng", required: true },
      { name: "note", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "name", header: "Tên nhóm hợp đồng", size: 260 },
      { accessorKey: "note", header: "Ghi chú", size: 360, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
  clients: {
    endpoint: "/Client",
    title: "chủ đầu tư",
    addLabel: "Thêm chủ đầu tư",
    idKey: "clientId",
    normalize: (item) => ({
      clientId: item?.clientId ?? item?.ClientId,
      clientName: item?.clientName ?? item?.ClientName ?? "",
      clientAddress: item?.clientAddress ?? item?.ClientAddress ?? "",
      clientContact: item?.clientContact ?? item?.ClientContact ?? "",
      clientPhone: item?.clientPhone ?? item?.ClientPhone ?? "",
      clientEmail: item?.clientEmail ?? item?.ClientEmail ?? "",
      taxCode: item?.taxCode ?? item?.TaxCode ?? "",
      notes: item?.notes ?? item?.Notes ?? "",
    }),
    toPayload: (values) => ({
      clientName: String(values.clientName || "").trim(),
      clientAddress: String(values.clientAddress || "").trim() || null,
      clientContact: String(values.clientContact || "").trim() || null,
      clientPhone: String(values.clientPhone || "").trim() || null,
      clientEmail: String(values.clientEmail || "").trim() || null,
      taxCode: String(values.taxCode || "").trim() || null,
      notes: String(values.notes || "").trim() || null,
    }),
    fields: [
      { name: "clientName", label: "Tên chủ đầu tư", required: true },
      { name: "taxCode", label: "Mã số thuế" },
      { name: "clientContact", label: "Người liên hệ" },
      { name: "clientPhone", label: "Số điện thoại" },
      { name: "clientEmail", label: "Email" },
      { name: "clientAddress", label: "Địa chỉ", multiline: true },
      { name: "notes", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "clientName", header: "Tên chủ đầu tư", size: 260 },
      { accessorKey: "taxCode", header: "Mã số thuế", size: 130, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "clientContact", header: "Người liên hệ", size: 170, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "clientPhone", header: "Điện thoại", size: 130, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "clientEmail", header: "Email", size: 180, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "clientAddress", header: "Địa chỉ", size: 300, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
  contractors: {
    endpoint: "/Contractor",
    title: "đơn vị thi công",
    addLabel: "Thêm đơn vị thi công",
    idKey: "contractorId",
    normalize: (item) => ({
      contractorId: item?.contractorId ?? item?.ContractorId,
      contractorName: item?.contractorName ?? item?.ContractorName ?? "",
      contractorAddress: item?.contractorAddress ?? item?.ContractorAddress ?? "",
      contactPerson: item?.contactPerson ?? item?.ContactPerson ?? "",
      contactPhone: item?.contactPhone ?? item?.ContactPhone ?? "",
      contactEmail: item?.contactEmail ?? item?.ContactEmail ?? "",
      notes: item?.notes ?? item?.Notes ?? "",
    }),
    toPayload: (values) => ({
      contractorName: String(values.contractorName || "").trim(),
      contractorAddress: String(values.contractorAddress || "").trim() || null,
      contactPerson: String(values.contactPerson || "").trim() || null,
      contactPhone: String(values.contactPhone || "").trim() || null,
      contactEmail: String(values.contactEmail || "").trim() || null,
      notes: String(values.notes || "").trim() || null,
    }),
    fields: [
      { name: "contractorName", label: "Tên đơn vị thi công", required: true },
      { name: "contactPerson", label: "Người liên hệ" },
      { name: "contactPhone", label: "Số điện thoại" },
      { name: "contactEmail", label: "Email" },
      { name: "contractorAddress", label: "Địa chỉ", multiline: true },
      { name: "notes", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "contractorName", header: "Tên đơn vị thi công", size: 280 },
      { accessorKey: "contactPerson", header: "Người liên hệ", size: 170, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "contactPhone", header: "Điện thoại", size: 130, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "contactEmail", header: "Email", size: 180, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "contractorAddress", header: "Địa chỉ", size: 320, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
  roleInContracts: {
    endpoint: "/RoleInContract",
    title: "vai trò trong hợp đồng",
    addLabel: "Thêm vai trò",
    idKey: "id",
    normalize: (item) => ({
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name ?? "",
      note: item?.note ?? item?.Note ?? "",
    }),
    toPayload: (values) => ({
      name: String(values.name || "").trim(),
      note: String(values.note || "").trim() || null,
    }),
    fields: [
      { name: "name", label: "Tên vai trò", required: true },
      { name: "note", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "name", header: "Tên vai trò", size: 260 },
      { accessorKey: "note", header: "Ghi chú", size: 360, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
  unitInContracts: {
    endpoint: "/UnitInContract",
    title: "đơn vị trong hợp đồng",
    addLabel: "Thêm đơn vị",
    idKey: "id",
    normalize: (item) => ({
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name ?? "",
      note: item?.note ?? item?.Note ?? "",
    }),
    toPayload: (values) => ({
      name: String(values.name || "").trim(),
      note: String(values.note || "").trim() || null,
    }),
    fields: [
      { name: "name", label: "Đơn vị", required: true },
      { name: "note", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "name", header: "Đơn vị", size: 260 },
      { accessorKey: "note", header: "Ghi chú", size: 360, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
  roleInSubcontracts: {
    endpoint: "/RoleInSubcontract",
    title: "vai trò trong giao khoán",
    addLabel: "Thêm vai trò",
    idKey: "id",
    normalize: (item) => ({
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name ?? "",
      note: item?.note ?? item?.Note ?? "",
    }),
    toPayload: (values) => ({
      name: String(values.name || "").trim(),
      note: String(values.note || "").trim() || null,
    }),
    fields: [
      { name: "name", label: "Tên vai trò", required: true },
      { name: "note", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "name", header: "Tên vai trò", size: 260 },
      { accessorKey: "note", header: "Ghi chú", size: 360, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
  unitInSubcontracts: {
    endpoint: "/UnitInSubcontract",
    title: "đơn vị trong giao khoán",
    addLabel: "Thêm đơn vị",
    idKey: "id",
    normalize: (item) => ({
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name ?? "",
      note: item?.note ?? item?.Note ?? "",
    }),
    toPayload: (values) => ({
      name: String(values.name || "").trim(),
      note: String(values.note || "").trim() || null,
    }),
    fields: [
      { name: "name", label: "Đơn vị", required: true },
      { name: "note", label: "Ghi chú", multiline: true },
    ],
    columns: [
      { accessorKey: "name", header: "Đơn vị", size: 260 },
      { accessorKey: "note", header: "Ghi chú", size: 360, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
  },
};

// Aliases for singular keys
catalogConfigs.roleInContract = catalogConfigs.roleInContracts;
catalogConfigs.unitInContract = catalogConfigs.unitInContracts;
catalogConfigs.roleInSubcontract = catalogConfigs.roleInSubcontracts;
catalogConfigs.unitInSubcontract = catalogConfigs.unitInSubcontracts;

export const normalizeDepartment = (item) => ({
  departmentId: item?.departmentId ?? item?.DepartmentId,
  departmentType: item?.departmentType ?? item?.DepartmentType ?? "",
  enterpriseId: item?.enterpriseId ?? item?.EnterpriseId ?? "",
  enterpriseName: item?.enterpriseName ?? item?.EnterpriseName ?? "",
  departmentCode: item?.departmentCode ?? item?.DepartmentCode ?? "",
  departmentName: item?.departmentName ?? item?.DepartmentName ?? "",
  description: item?.description ?? item?.Description ?? "",
  phone: item?.phone ?? item?.Phone ?? "",
  email: item?.email ?? item?.Email ?? "",
  leaderUserId: item?.leaderUserId ?? item?.LeaderUserId ?? "",
  leaderUserName: item?.leaderUserName ?? item?.LeaderUserName ?? "",
  leaderFullName: item?.leaderFullName ?? item?.LeaderFullName ?? "",
});

export const departmentLeaderOptions = (options, row) => {
  const currentId = row?.leaderUserId;
  const currentOption = currentId && !options.some((option) => Number(option.value) === Number(currentId))
    ? [{ value: currentId, label: row.leaderFullName || row.leaderUserName || "Người phụ trách hiện tại" }]
    : [];
  return [{ value: "", label: "Không chọn" }, ...options, ...currentOption];
};

export const normalizeDepartmentUnit = (item) => ({
  departmentUnitId: item?.departmentUnitId ?? item?.DepartmentUnitId,
  departmentId: item?.departmentId ?? item?.DepartmentId,
  departmentUnitCode: item?.departmentUnitCode ?? item?.DepartmentUnitCode ?? "",
  departmentUnitName: item?.departmentUnitName ?? item?.DepartmentUnitName ?? "",
  description: item?.description ?? item?.Description ?? "",
  phone: item?.phone ?? item?.Phone ?? "",
  email: item?.email ?? item?.Email ?? "",
  leaderUserId: item?.leaderUserId ?? item?.LeaderUserId ?? "",
  leaderUserName: item?.leaderUserName ?? item?.LeaderUserName ?? "",
  leaderFullName: item?.leaderFullName ?? item?.LeaderFullName ?? "",
});

export const normalizeExecutionGroup = (item) => ({
  subcontractExecutionGroupId: item?.subcontractExecutionGroupId ?? item?.SubcontractExecutionGroupId,
  subcontractExecutionGroupCode: item?.subcontractExecutionGroupCode ?? item?.SubcontractExecutionGroupCode ?? "",
  subcontractExecutionGroupName: item?.subcontractExecutionGroupName ?? item?.SubcontractExecutionGroupName ?? "",
  notes: item?.notes ?? item?.Notes ?? "",
  isActive: normalizeBool(item?.isActive ?? item?.IsActive),
  leaderFullName: item?.leaderFullName ?? item?.LeaderFullName ?? "",
  leaderUserName: item?.leaderUserName ?? item?.LeaderUserName ?? "",
  totalMembers: item?.totalMembers ?? item?.TotalMembers ?? 0,
});

export const normalizeExecutionGroupUser = (item) => ({
  subcontractExecutionGroupUserId: item?.subcontractExecutionGroupUserId ?? item?.SubcontractExecutionGroupUserId,
  subcontractExecutionGroupId: item?.subcontractExecutionGroupId ?? item?.SubcontractExecutionGroupId,
  userId: item?.userId ?? item?.UserId,
  userName: item?.userName ?? item?.UserName ?? "",
  fullName: item?.fullName ?? item?.FullName ?? "",
  email: item?.email ?? item?.Email ?? "",
  roleName: item?.roleName ?? item?.RoleName ?? "",
  isLeader: normalizeBool(item?.isLeader ?? item?.IsLeader),
  displayOrder: item?.displayOrder ?? item?.DisplayOrder ?? 0,
  notes: item?.notes ?? item?.Notes ?? "",
});
